import { format, parseISO } from "date-fns";
import { Copy, Search, ShieldCheck, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { PageContainer, PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { assignableTiers, tierLabelKey } from "@/features/admin/tiers";
import { deriveUserStats } from "@/features/admin/userStats";
import { StatusBadge } from "@/features/engagement/components/StatusBadge";
import { deriveEngagementStatus } from "@/features/engagement/utils/deriveEngagementStatus";
import { useHistoryStorage } from "@/services";
import type { EngagementHistoryEntry, UserProfile } from "@/types";

const UNDO_DURATION_MS = 10_000;

// SPEC-ADMIN3 — lazy-load the Usage tab so recharts (~400 KB) is split out of
// the main bundle and only fetched when an admin opens the "Kullanım" tab.
const UsagePanel = React.lazy(() => import("./UsagePanel"));

// SPEC-SETTINGS — lazy-load the pending tier-requests review tab.
const PendingTierRequestsPanel = React.lazy(
  () => import("./PendingTierRequestsPanel"),
);

interface AdminNavState {
  adminView: true;
  ownerEmail: string | null;
}

/**
 * SPEC-23 — admin panel.
 * SPEC-24 — admin cards now carry the FULL HistoryPage action set
 * (Aç / Bilgileri Düzenle / Sözleşmeleri Düzenle / Çoğalt / Cross-check /
 * Sil), all stamped with `state: { adminView, ownerEmail }` so the
 * AdminViewBanner stays lit on every borrowed-engagement page.
 *
 * Layout: a left-side searchable user list backed by `listUserProfiles`;
 * selecting a user fetches their engagements via
 * `listEngagementsByOwner` and renders HistoryPage-style cards on the
 * right.
 */
export function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storage = useHistoryStorage();

  const [users, setUsers] = React.useState<UserProfile[] | null>(null);
  const [userQuery, setUserQuery] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<UserProfile | null>(
    null,
  );
  const [engagements, setEngagements] = React.useState<
    EngagementHistoryEntry[] | null
  >(null);
  const [deleteCandidate, setDeleteCandidate] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [savingTier, setSavingTier] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    storage
      .listUserProfiles()
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch((err) => {
        console.error("[admin] listUserProfiles failed", err);
        toast.error(t("admin.users.loadError"));
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [storage, t]);

  const refreshEngagements = React.useCallback(async (): Promise<void> => {
    if (!selectedUser) return;
    try {
      const rows = await storage.listEngagementsByOwner(selectedUser.id);
      setEngagements(rows);
    } catch (err) {
      console.error("[admin] refresh failed", err);
      toast.error(t("admin.engagements.loadError"));
      setEngagements([]);
    }
  }, [selectedUser, storage, t]);

  React.useEffect(() => {
    if (!selectedUser) {
      setEngagements(null);
      return;
    }
    setEngagements(null);
    void refreshEngagements();
  }, [selectedUser, refreshEngagements]);

  const filteredUsers = React.useMemo(() => {
    if (!users) return [] as UserProfile[];
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const hay = `${u.email ?? ""} ${u.fullName ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, userQuery]);

  const ownerEmail = selectedUser?.email ?? null;

  const navState = React.useCallback(
    (): AdminNavState => ({ adminView: true, ownerEmail }),
    [ownerEmail],
  );

  async function handleTierChange(newTier: string): Promise<void> {
    if (!selectedUser) return;
    setSavingTier(true);
    try {
      await storage.setUserTier(selectedUser.id, newTier);
      // reflect immediately in both selectedUser and the users list
      setSelectedUser({ ...selectedUser, tier: newTier });
      setUsers((prev) =>
        prev
          ? prev.map((u) =>
              u.id === selectedUser.id ? { ...u, tier: newTier } : u,
            )
          : prev,
      );
      toast.success(
        t("admin.tier.success", {
          tier: t(tierLabelKey(newTier) as never) as string,
        }),
      );
    } catch (err) {
      const code = (err as Error).message;
      const key =
        code === "TIER_NOT_ADMIN"
          ? "admin.tier.error.notAdmin"
          : code === "TIER_UNLIMITED_ADMIN_ONLY"
            ? "admin.tier.error.unlimitedAdminOnly"
            : code === "TIER_INVALID"
              ? "admin.tier.error.invalid"
              : code === "TIER_USER_NOT_FOUND"
                ? "admin.tier.error.userNotFound"
                : "admin.tier.error.generic";
      toast.error(t(key));
      console.error("[admin] setUserTier failed", err);
    } finally {
      setSavingTier(false);
    }
  }

  function handleOpen(engagement: EngagementHistoryEntry): void {
    const target = engagement.lastResult
      ? `/engagements/${engagement.id}/results/balance-sheet`
      : `/engagements/${engagement.id}/contracts`;
    if (!engagement.lastResult) {
      toast.info(t("history.open.notCalculated"));
    }
    navigate(target, { state: navState() });
  }

  function handleEditInfo(engagement: EngagementHistoryEntry): void {
    navigate(`/engagements/${engagement.id}/edit`, { state: navState() });
  }

  function handleEditContracts(engagement: EngagementHistoryEntry): void {
    navigate(`/engagements/${engagement.id}/contracts`, { state: navState() });
  }

  async function handleDuplicate(
    engagement: EngagementHistoryEntry,
  ): Promise<void> {
    const now = new Date().toISOString();
    const copy: EngagementHistoryEntry = {
      ...engagement,
      id: `eng-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      clientName: `${engagement.clientName}${t("history.duplicate.suffix")}`,
      contracts: engagement.contracts.map((c) => (c ? { ...c } : c)),
      contractsDirty: engagement.contractsDirty
        ? [...engagement.contractsDirty]
        : undefined,
      lastResult: undefined,
      isStale: false,
      status: "draft",
      calculatedAt: undefined,
      staleSince: undefined,
      lastCalculateError: undefined,
      isDeleted: false,
      deletedAt: undefined,
      createdAt: now,
      updatedAt: now,
    };
    try {
      await storage.saveEngagement(copy);
      await refreshEngagements();
      // SPEC-24 §3 — the duplicate is stamped with the ADMIN's owner_id
      // (DB DEFAULT auth.uid()), so it lands in the admin's own
      // dashboard, NOT the selected user's list. Surface this clearly
      // so admins aren't surprised when the copy "disappears" from the
      // user they were borrowing.
      toast.success(t("admin.duplicate.success", { name: copy.clientName }), {
        description: t("admin.duplicate.belongsToAdmin"),
      });
    } catch (err) {
      console.error("[admin] duplicate failed", err);
      toast.error(t("admin.engagements.loadError"));
    }
  }

  async function handleCrossCheck(
    engagement: EngagementHistoryEntry,
  ): Promise<void> {
    try {
      const { buildCrossCheckWorkbook, crossCheckFilename } = await import(
        "@/features/dev-tools/crossCheckExport"
      );
      const { saveAs } = await import("file-saver");
      const buffer = await buildCrossCheckWorkbook(engagement);
      saveAs(new Blob([buffer]), crossCheckFilename(engagement));
      toast.success("Cross-check Excel indirildi");
    } catch (err) {
      console.error("[admin] crosscheck export failed", err);
      const msg = (err as Error)?.message ?? "";
      const staleChunk =
        /dynamically imported module|module script failed|Failed to fetch/i.test(
          msg,
        );
      toast.error(
        staleChunk
          ? "Uygulama güncellendi — lütfen sayfayı yenileyin (Ctrl+Shift+R) ve tekrar deneyin."
          : `Cross-check Excel oluşturulamadı: ${msg || "bilinmeyen hata"}`,
      );
    }
  }

  async function confirmDelete(): Promise<void> {
    const target = deleteCandidate;
    if (!target) return;
    setDeleteCandidate(null);
    try {
      await storage.deleteEngagement(target.id);
      await refreshEngagements();
      // Mirror HistoryPage's 10-second undo affordance. The restore
      // mirror's deleteEngagement reclaim: a clean draft re-appears,
      // not a stale snapshot.
      toast(t("history.delete.success", { name: target.clientName }), {
        duration: UNDO_DURATION_MS,
        action: {
          label: t("history.delete.undo"),
          onClick: () => {
            const restored: EngagementHistoryEntry = {
              ...target,
              lastResult: undefined,
              status: "draft",
              isStale: false,
              calculatedAt: undefined,
              staleSince: undefined,
              lastCalculateError: undefined,
              isDeleted: false,
              deletedAt: undefined,
              updatedAt: new Date().toISOString(),
            };
            void storage
              .saveEngagement(restored)
              .then(() => refreshEngagements())
              .then(() =>
                toast.success(
                  t("history.delete.restored", { name: target.clientName }),
                ),
              )
              .catch((err) => {
                console.error("[admin] restore failed", err);
                toast.error(t("admin.engagements.loadError"));
              });
          },
        },
      });
    } catch (err) {
      console.error("[admin] delete failed", err);
      toast.error(t("admin.engagements.loadError"));
    }
  }

  return (
    <PageContainer>
      <PageHeader primary={t("admin.page.title")} />

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users" data-testid="admin-tab-users">
            {t("admin.tabs.users")}
          </TabsTrigger>
          <TabsTrigger value="usage" data-testid="admin-tab-usage">
            {t("admin.tabs.usage")}
          </TabsTrigger>
          <TabsTrigger
            value="tierRequests"
            data-testid="admin-tab-tier-requests"
          >
            {t("admin.tabs.tierRequests")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
            <UserList
              users={filteredUsers}
              loading={users === null}
              totalCount={users?.length ?? 0}
              query={userQuery}
              onQueryChange={setUserQuery}
              selectedId={selectedUser?.id ?? null}
              onSelect={setSelectedUser}
            />

            <div className="flex flex-col gap-4">
              {selectedUser ? (
                <UserProfileCard
                  user={selectedUser}
                  engagements={engagements}
                  savingTier={savingTier}
                  onTierChange={(v) => void handleTierChange(v)}
                />
              ) : null}
              <EngagementsPanel
                selectedUser={selectedUser}
                engagements={engagements}
                onOpen={handleOpen}
                onEditInfo={handleEditInfo}
                onEditContracts={handleEditContracts}
                onDuplicate={(e) => void handleDuplicate(e)}
                onCrossCheck={(e) => void handleCrossCheck(e)}
                onDelete={setDeleteCandidate}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="usage">
          <React.Suspense
            fallback={
              <div className="text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            }
          >
            <UsagePanel />
          </React.Suspense>
        </TabsContent>

        <TabsContent value="tierRequests">
          <React.Suspense
            fallback={
              <div className="text-sm text-muted-foreground">
                {t("common.loading")}
              </div>
            }
          >
            <PendingTierRequestsPanel />
          </React.Suspense>
        </TabsContent>
      </Tabs>

      <DeleteConfirmDialog
        candidate={deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={() => void confirmDelete()}
      />
    </PageContainer>
  );
}

// ─── User list ────────────────────────────────────────────────────────────

interface UserListProps {
  users: UserProfile[];
  loading: boolean;
  totalCount: number;
  query: string;
  onQueryChange: (v: string) => void;
  selectedId: string | null;
  onSelect: (u: UserProfile) => void;
}

function UserList({
  users,
  loading,
  totalCount,
  query,
  onQueryChange,
  selectedId,
  onSelect,
}: UserListProps) {
  const { t } = useTranslation();

  return (
    <Card className="flex max-h-[70vh] flex-col p-3" data-testid="admin-user-list">
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t("admin.users.search")}
          className="pl-8"
          data-testid="admin-user-search"
        />
      </div>
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="space-y-2 py-2">
            <div className="h-9 animate-pulse rounded bg-muted/40" />
            <div className="h-9 animate-pulse rounded bg-muted/40" />
            <div className="h-9 animate-pulse rounded bg-muted/40" />
          </div>
        ) : users.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm italic text-muted-foreground">
            {totalCount === 0
              ? t("admin.users.empty")
              : t("admin.users.filteredEmpty")}
          </p>
        ) : (
          <ul className="space-y-1">
            {users.map((u) => {
              const label = u.fullName ?? u.email ?? u.id;
              const sub = u.fullName ? u.email : null;
              const isSelected = u.id === selectedId;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(u)}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50"
                    }`}
                    data-testid={`admin-user-item-${u.id}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {label}
                      </span>
                      {sub ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {sub}
                        </span>
                      ) : null}
                    </span>
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning-foreground">
                        <ShieldCheck className="h-3 w-3" />
                        {t("admin.users.adminBadge")}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ─── Engagements panel ─────────────────────────────────────────────────────

interface EngagementsPanelProps {
  selectedUser: UserProfile | null;
  engagements: EngagementHistoryEntry[] | null;
  onOpen: (engagement: EngagementHistoryEntry) => void;
  onEditInfo: (engagement: EngagementHistoryEntry) => void;
  onEditContracts: (engagement: EngagementHistoryEntry) => void;
  onDuplicate: (engagement: EngagementHistoryEntry) => void;
  onCrossCheck: (engagement: EngagementHistoryEntry) => void;
  onDelete: (engagement: EngagementHistoryEntry) => void;
}

function EngagementsPanel({
  selectedUser,
  engagements,
  onOpen,
  onEditInfo,
  onEditContracts,
  onDuplicate,
  onCrossCheck,
  onDelete,
}: EngagementsPanelProps) {
  const { t } = useTranslation();

  if (!selectedUser) {
    return (
      <Card className="flex min-h-[200px] items-center justify-center p-6">
        <p className="text-sm italic text-muted-foreground">
          {t("admin.engagements.selectPrompt")}
        </p>
      </Card>
    );
  }

  if (engagements === null) {
    return (
      <div className="space-y-3" data-testid="admin-engagements-loading">
        <div className="h-20 animate-pulse rounded bg-muted/40" />
        <div className="h-20 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  if (engagements.length === 0) {
    return (
      <Card className="flex min-h-[200px] items-center justify-center p-6">
        <p className="text-sm italic text-muted-foreground">
          {t("admin.engagements.empty", {
            name:
              selectedUser.fullName ?? selectedUser.email ?? selectedUser.id,
          })}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-engagement-list">
      <div className="text-sm text-muted-foreground">
        {t("admin.engagements.count", {
          count: engagements.length,
          name: selectedUser.fullName ?? selectedUser.email ?? selectedUser.id,
        })}
      </div>
      {engagements.map((engagement) => (
        <AdminEngagementCard
          key={engagement.id}
          engagement={engagement}
          onOpen={() => onOpen(engagement)}
          onEditInfo={() => onEditInfo(engagement)}
          onEditContracts={() => onEditContracts(engagement)}
          onDuplicate={() => onDuplicate(engagement)}
          onCrossCheck={() => onCrossCheck(engagement)}
          onDelete={() => onDelete(engagement)}
        />
      ))}
    </div>
  );
}

// ─── UserProfileCard (SPEC-ADMIN2 — user "künye" + tier control) ──────────

interface UserProfileCardProps {
  user: UserProfile;
  engagements: EngagementHistoryEntry[] | null; // null = still loading
  savingTier: boolean;
  onTierChange: (tier: string) => void;
}

function UserProfileCard({
  user,
  engagements,
  savingTier,
  onTierChange,
}: UserProfileCardProps) {
  const { t } = useTranslation();
  const stats = React.useMemo(
    () => deriveUserStats(engagements),
    [engagements],
  );
  const loading = engagements === null;

  // Always render the user's CURRENT tier as an option even when it is not
  // assignable for their role (e.g. a non-admin row still carrying 'unlimited'
  // from a prior grant or a later admin→user demotion). Without this the
  // controlled Select trigger renders BLANK and the admin can't see the
  // current tier. The DB still enforces which tiers may be (re)assigned.
  const assignable: string[] = assignableTiers(user.role);
  const options = assignable.includes(user.tier)
    ? assignable
    : [...assignable, user.tier];

  const isAdmin = user.role === "admin";
  const name = user.fullName ?? user.email ?? user.id;

  // While engagements are loading, show a subtle pulsing em-dash — NOT 0 —
  // so a genuine 0 (loaded, empty) stays distinguishable from "loading".
  const skeleton = (
    <span className="inline-block w-8 animate-pulse text-muted-foreground">
      —
    </span>
  );

  async function copyEmail(): Promise<void> {
    if (!user.email) return;
    await navigator.clipboard.writeText(user.email);
    toast.success(t("admin.profileCard.emailCopied"));
  }

  return (
    <Card className="p-4" data-testid="admin-profile-card">
      {/* 1. Header: name + admin badge */}
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 truncate font-semibold" title={name}>
          {name}
        </p>
        {isAdmin ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning-foreground">
            <ShieldCheck className="h-3 w-3" />
            {t("admin.users.adminBadge")}
          </span>
        ) : null}
      </div>

      {/* 2. Email row + copy button */}
      {user.email ? (
        <div className="mt-1 flex items-center gap-1">
          <span className="min-w-0 truncate text-sm text-muted-foreground">
            {user.email}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 shrink-0 p-0"
                  onClick={() => void copyEmail()}
                  aria-label={t("admin.profileCard.copyEmail")}
                  data-testid="admin-profile-copy-email"
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("admin.profileCard.copyEmail")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : null}

      {/* 3. Meta grid */}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-xs text-muted-foreground">
          {t("admin.profileCard.role")}
        </span>
        <span className="text-sm">
          {isAdmin
            ? t("admin.profileCard.roleAdmin")
            : t("admin.profileCard.roleUser")}
        </span>

        <span className="text-xs text-muted-foreground">
          {t("admin.profileCard.created")}
        </span>
        <span className="text-sm">{safeFormat(user.createdAt, "dd.MM.yyyy")}</span>

        <span className="text-xs text-muted-foreground">
          {t("admin.profileCard.engagementCount")}
        </span>
        <span className="text-sm">
          {loading ? skeleton : stats.engagementCount}
        </span>

        <span className="text-xs text-muted-foreground">
          {t("admin.profileCard.totalContracts")}
        </span>
        <span className="text-sm">
          {loading ? skeleton : stats.totalContracts}
        </span>

        <span className="text-xs text-muted-foreground">
          {t("admin.profileCard.lastActivity")}
        </span>
        <span className="text-sm">
          {loading
            ? skeleton
            : stats.lastActivity
              ? safeFormat(stats.lastActivity, "dd.MM.yyyy HH:mm")
              : t("admin.profileCard.never")}
        </span>
      </div>

      {/* 4. Divider */}
      <div className="my-3 h-px bg-border" />

      {/* 5. Tier control row */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">{t("admin.tier.label")}</p>
        <Select
          value={user.tier}
          onValueChange={onTierChange}
          disabled={savingTier}
        >
          <SelectTrigger className="w-44" data-testid="admin-tier-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {t(tierLabelKey(opt) as never) as string}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
}

// ─── AdminEngagementCard (HistoryPage parity) ─────────────────────────────

interface AdminEngagementCardProps {
  engagement: EngagementHistoryEntry;
  onOpen: () => void;
  onEditInfo: () => void;
  onEditContracts: () => void;
  onDuplicate: () => void;
  onCrossCheck: () => void;
  onDelete: () => void;
}

function AdminEngagementCard({
  engagement,
  onOpen,
  onEditInfo,
  onEditContracts,
  onDuplicate,
  onCrossCheck,
  onDelete,
}: AdminEngagementCardProps) {
  const { t } = useTranslation();
  const status = deriveEngagementStatus(engagement);
  const startDisplay = safeFormat(engagement.auditPeriodStart, "dd.MM.yyyy");
  const endDisplay = safeFormat(engagement.auditPeriodEnd, "dd.MM.yyyy");
  const lastCalc = engagement.lastResult?.calculatedAt;

  return (
    <Card
      className="p-4 transition-shadow hover:shadow-md"
      data-testid={`admin-engagement-card-${engagement.id}`}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <h2
          className="truncate text-lg font-semibold"
          title={engagement.clientName}
        >
          {engagement.clientName}
        </h2>
        <StatusBadge status={status} />
      </div>
      <p className="text-sm text-muted-foreground">
        {startDisplay} — {endDisplay} · {engagement.functionalCurrency} ·{" "}
        {t("history.card.contractCount", { count: engagement.contractCount })}
      </p>
      {lastCalc ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {t("history.card.lastCalculated", {
            date: safeFormat(lastCalc, "dd.MM.yyyy HH:mm"),
          })}
        </p>
      ) : (
        <p className="mt-1 text-xs italic text-muted-foreground">
          {t("history.card.neverCalculated")}
        </p>
      )}
      {/* Mirror HistoryPage's button hierarchy: primary "Aç" + two
          outline edits + two ghost-icon actions + cross-check parity. */}
      <TooltipProvider>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={onOpen}
            data-testid={`admin-engagement-open-${engagement.id}`}
          >
            {engagement.lastResult
              ? t("history.action.openResults")
              : t("history.action.openContracts")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditInfo}
            data-testid={`admin-engagement-edit-info-${engagement.id}`}
          >
            {t("history.action.editInfo")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditContracts}
            data-testid={`admin-engagement-edit-contracts-${engagement.id}`}
          >
            {t("history.action.editContracts")}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDuplicate}
                aria-label={t("history.action.duplicate")}
                data-testid={`admin-engagement-duplicate-${engagement.id}`}
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("history.action.duplicate")}</TooltipContent>
          </Tooltip>
          {/* TEMP: cross-check input export — mirrors HistoryPage parity. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onCrossCheck}
            data-testid={`admin-engagement-crosscheck-${engagement.id}`}
          >
            🔬 Cross-check Excel
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                aria-label={t("history.action.delete")}
                data-testid={`admin-engagement-delete-${engagement.id}`}
              >
                <Trash2
                  className="h-4 w-4 text-destructive"
                  aria-hidden="true"
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("history.action.delete")}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </Card>
  );
}

// ─── DeleteConfirmDialog (mirrors HistoryPage's) ──────────────────────────

interface DeleteConfirmDialogProps {
  candidate: EngagementHistoryEntry | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function DeleteConfirmDialog({
  candidate,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const open = candidate !== null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="admin-delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{t("history.delete.confirm.title")}</DialogTitle>
          <DialogDescription>
            {t("history.delete.confirm.body", {
              name: candidate?.clientName ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="admin-delete-confirm-cancel"
          >
            {t("history.delete.confirm.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            data-testid="admin-delete-confirm-delete"
          >
            {t("history.delete.confirm.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function safeFormat(iso: string, fmt: string): string {
  try {
    const date = parseISO(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return format(date, fmt);
  } catch {
    return iso;
  }
}
